import type { ImplementationKind } from "@finch-api/common/dist/internal/connect/authorize";
import {
	type InstructionConfig,
	SCOPED_AUTH_INSTRUCTIONS,
	ScopeCategory,
} from "constants/scoped-auth-instructions";
import type { PayrollProviderId } from "constants/types";

/**
 * Determines the scope category from the products array.
 * Priority order:
 * 1. benadmin: if products includes a Ben Admin scope
 * 2. benefits: if products includes 'employer:benefits'
 * 3. pay_and_organization: if products includes 'employer:payment' or 'employer:pay_statement'
 * 4. organization_only: if only company/directory scopes
 * 5. base: default fallback
 */
export function generateScopeForProvider(
	_provider: PayrollProviderId,
	_impl: ImplementationKind,
	products: string[],
): ScopeCategory {
	// Helper function to check if product matches (with or without employer: prefix)
	const hasProduct = (productName: string): boolean => {
		return (
			products.includes(productName) ||
			products.includes(`employer:${productName}`)
		);
	};

	// Check for Ben Admin scopes first (highest priority)
	if (
		hasProduct("plans") ||
		hasProduct("plans_enrollments") ||
		hasProduct("plans_dependents")
	) {
		return ScopeCategory.BENADMIN;
	}

	if (hasProduct("benefits")) {
		return ScopeCategory.BENEFITS;
	}

	// Check for pay-related scopes
	if (hasProduct("payment") || hasProduct("pay_statement")) {
		return ScopeCategory.PAY_AND_ORGANIZATION;
	}

	// Check if organization scopes are present (company, directory, employment, individual)
	// Products can be with or without employer: prefix
	// If company or directory are present (even with employment/individual), use organization_only
	const hasOrganizationScopes = products.some(
		(product) =>
			product === "company" ||
			product === "directory" ||
			product === "employer:company" ||
			product === "employer:directory",
	);

	// If we have organization scopes (company/directory) and no pay/benefits scopes, use organization_only
	// This includes cases where employment/individual are also present
	const isOrganizationOnly =
		hasOrganizationScopes &&
		!hasProduct("payment") &&
		!hasProduct("pay_statement") &&
		!hasProduct("benefits") &&
		!hasProduct("plans") &&
		!hasProduct("plans_enrollments") &&
		!hasProduct("plans_dependents");

	if (isOrganizationOnly) {
		return ScopeCategory.ORGANIZATION_ONLY;
	}

	// Default fallback
	return ScopeCategory.BASE;
}

/**
 * Retrieves instruction config with fallback logic:
 * 1. Tries scope-specific config first
 * 2. Falls back to base config
 * 3. Returns undefined if neither exists
 *
 * Merges innerHtmlToScroll from implementation level and scope level.
 * Implementation-level innerHtmlToScroll is common across all scopes.
 * Scope-level innerHtmlToScroll overrides/merges with implementation-level.
 */
export function getInstructionConfig(
	provider: PayrollProviderId,
	impl: ImplementationKind,
	scope: ScopeCategory,
): InstructionConfig | undefined {
	const providerConfig = SCOPED_AUTH_INSTRUCTIONS[provider];
	if (!providerConfig) {
		return undefined;
	}

	const implConfig = providerConfig[impl];
	if (!implConfig) {
		return undefined;
	}

	// Get implementation-level innerHtmlToScroll (common across all scopes)
	const implInnerHtmlToScroll = implConfig.innerHtmlToScroll || {};

	// Try scope-specific config first
	const scopeConfig = implConfig[scope];
	if (scopeConfig) {
		const mergedInnerHtmlToScroll = {
			...implInnerHtmlToScroll,
			...(scopeConfig.innerHtmlToScroll || {}),
		};
		return {
			url: scopeConfig.url,
			innerHtmlToScroll: mergedInnerHtmlToScroll,
		};
	}

	// Fall back to base config
	const baseConfig = implConfig[ScopeCategory.BASE];
	if (baseConfig) {
		const mergedInnerHtmlToScroll = {
			...implInnerHtmlToScroll,
			...(baseConfig.innerHtmlToScroll || {}),
		};
		return {
			url: baseConfig.url,
			innerHtmlToScroll: mergedInnerHtmlToScroll,
		};
	}

	return undefined;
}

/**
 * Validates an InstructionConfig.
 * - URL must start with https://support.tryfinch.com
 * - Throws descriptive errors on validation failure
 */
export function validateConfig(config: InstructionConfig): void {
	if (!config.url) {
		throw new Error("InstructionConfig must have a url property");
	}

	if (!config.url.startsWith("https://support.tryfinch.com")) {
		throw new Error(
			`InstructionConfig url must start with 'https://support.tryfinch.com', got: ${config.url}`,
		);
	}
}
